import { useQuery } from '@tanstack/react-query'
import { useNetworkEnvironment } from '@/hooks/useNetworkEnvironment'
import { auditService, type OrderStateEventParams } from '@/services/admin/auditService'
import { monitoringService } from '@/services/admin/monitoringService'
import { orderService } from '@/services/admin/orderService'
import { orderConsistencyQueryKeys } from './orderConsistencyQueryKeys'

export { orderConsistencyQueryKeys } from './orderConsistencyQueryKeys'

export function useOrderStateEvents(
  params: OrderStateEventParams,
  options?: { enabled?: boolean }
) {
  const { currentEnv } = useNetworkEnvironment()
  return useQuery({
    queryKey: orderConsistencyQueryKeys.stateEvents(currentEnv, params),
    queryFn: () => auditService.getOrderStateEvents(params, currentEnv),
    enabled: options?.enabled ?? true,
    staleTime: 0,
  })
}

export function useOrderSideEffectJobSummary(options?: { enabled?: boolean }) {
  const { currentEnv } = useNetworkEnvironment()
  return useQuery({
    queryKey: orderConsistencyQueryKeys.sideEffectSummary(currentEnv),
    queryFn: () => monitoringService.getOrderSideEffectJobSummary(currentEnv),
    enabled: options?.enabled ?? true,
    refetchInterval: options?.enabled === false ? false : 30_000,
    staleTime: 15_000,
  })
}

export function useOrderSideEffectJobs(
  params: {
    page?: number
    limit?: number
    orderId?: string
    status?: string
    jobType?: string
  },
  options?: { enabled?: boolean }
) {
  const { currentEnv } = useNetworkEnvironment()
  return useQuery({
    queryKey: orderConsistencyQueryKeys.sideEffectJobs(currentEnv, params),
    queryFn: () => monitoringService.getOrderSideEffectJobs(params, currentEnv),
    enabled: options?.enabled ?? true,
    staleTime: 0,
  })
}

export function useOrderScopedSideEffectJobs(
  orderId: string | null,
  params: { limit?: number; cursor?: string; status?: string; jobType?: string },
  options?: { enabled?: boolean }
) {
  const { currentEnv } = useNetworkEnvironment()
  return useQuery({
    queryKey: orderConsistencyQueryKeys.orderSideEffectJobs(currentEnv, orderId, params),
    queryFn: () => orderService.getOrderSideEffectJobs(orderId as string, params, currentEnv),
    enabled: Boolean(orderId) && (options?.enabled ?? true),
    staleTime: 0,
  })
}

export function useOrderStateConsistency(
  orderId: string | null,
  options?: { enabled?: boolean }
) {
  const { currentEnv } = useNetworkEnvironment()
  return useQuery({
    queryKey: orderConsistencyQueryKeys.stateConsistency(currentEnv, orderId),
    queryFn: () => orderService.getOrderStateConsistency(orderId as string, currentEnv),
    enabled: Boolean(orderId) && (options?.enabled ?? true),
    staleTime: 0,
  })
}

export function useOrderAddressDoubleCheck(
  orderId: string | null,
  options?: { enabled?: boolean }
) {
  const { currentEnv } = useNetworkEnvironment()
  return useQuery({
    queryKey: orderConsistencyQueryKeys.addressDoubleCheck(currentEnv, orderId),
    queryFn: () => orderService.getOrderAddressDoubleCheck(orderId as string, currentEnv),
    enabled: Boolean(orderId) && (options?.enabled ?? true),
    staleTime: 0,
  })
}

export function useSolanaOrderGovernanceState(
  orderId: string | null,
  options?: { enabled?: boolean }
) {
  const { currentEnv } = useNetworkEnvironment()
  const enabled = Boolean(orderId) && (options?.enabled ?? true)
  return useQuery({
    queryKey: orderConsistencyQueryKeys.solanaGovernanceState(currentEnv, orderId),
    queryFn: () => orderService.getSolanaOrderGovernanceState(orderId as string, currentEnv),
    enabled,
    staleTime: 0,
    refetchInterval: enabled ? 10_000 : false,
  })
}
